import { useOrgContext } from '@/org/contexts/OrgContext'
import { Mentionable, MentionEntities } from '@rolebase/editor/react'
import { useMemo } from 'react'

// What can be mentioned in a text: the org's people (@) and, since #92, all
// its roles and circles (#), each by its circle id. A role whose name another
// role shares is told apart by its circle in the suggestion list only.
export default function useMentionables(): Mentionable[] {
  const { orgData } = useOrgContext()

  return useMemo(() => {
    if (!orgData) return []

    const members: Mentionable[] = orgData.members.map((member) => ({
      entity: MentionEntities.Member,
      id: member.id,
      name: member.name,
    }))

    const named = orgData.circles.map((circle) => ({
      circle,
      name: orgData.getRole(circle.roleId)?.name ?? '',
    }))
    const count = new Map<string, number>()
    named.forEach(({ name }) => count.set(name, (count.get(name) ?? 0) + 1))

    const roles: Mentionable[] = named
      .filter(({ name }) => name !== '')
      .map(({ circle, name }) => {
        const parent = orgData.getCircle(circle.parentId ?? undefined)
        const parentName = parent && orgData.getRole(parent.roleId)?.name
        return {
          entity: MentionEntities.Role,
          id: circle.id,
          name,
          hint: (count.get(name) ?? 0) > 1 ? parentName : undefined,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))

    return [...members, ...roles]
  }, [orgData])
}
